import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, DataTable, Text, Link, Box, InlineStack, Button, ButtonGroup, Banner } from '@shopify/polaris';
import { CardHeading } from '../cards/CardHeading';
import { formatToCurrency } from '../functions';
import { useAuthStore } from '../../../context/useStore';
import PaymentIntentBadge from './PaymentIntentBadge';
import { intentApi, intentMeta, formatIban, signingTarget } from './paymentIntents';

/*
  Ponto payments created for one supplier invoice (payment intents), shown on the invoice page.
  Renders nothing when there are none or when the user has no banking access (the API answers 403).

  invoiceId     Faktuurin.faktuurinvolgnummer
  refreshToken  bump to reload (after the payment modal did something)
  onChanged     called after a cancel / refresh changed a payment (the page reloads the invoice)
*/
export default function PaymentIntentsCard({ invoiceId, refreshToken = 0, onChanged }) {
  const { user } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [intents, setIntents] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    if (!user?.bankingAccess || !invoiceId || invoiceId === 'new') return;
    const result = await intentApi(`/invoice/${invoiceId}`);
    if (result.ok && Array.isArray(result.data)) setIntents(result.data);
  }, [invoiceId, user?.bankingAccess]);

  useEffect(() => {
    load();
  }, [load, refreshToken]);

  // Ponto sends the signer back here with ?ponto=signed: pull the fresh status straight away instead of waiting for the hourly sync
  useEffect(() => {
    if (searchParams.get('ponto') !== 'signed' || !user?.bankingAccess || !invoiceId || invoiceId === 'new') return;
    let cancelled = false;
    (async () => {
      const current = await intentApi(`/invoice/${invoiceId}`);
      const active = current.ok && Array.isArray(current.data) ? current.data.filter((intent) => intent.active) : [];
      for (const intent of active) await intentApi(`/${intent.PontoPaymentID}/refresh`, { method: 'POST' });
      if (cancelled) return;
      searchParams.delete('ponto');
      setSearchParams(searchParams, { replace: true });
      setMessage({ tone: 'success', text: 'Back from Ponto — payment status refreshed.' });
      await load();
      onChanged?.();
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId, user?.bankingAccess]);

  if (intents.length === 0) return null;

  const act = async (intent, action) => {
    setBusyId(intent.PontoPaymentID);
    setMessage(null);
    const result = await intentApi(`/${intent.PontoPaymentID}/${action}`, { method: 'POST' });
    setBusyId(null);
    if (!result.ok) {
      setMessage({ tone: 'critical', text: result.data?.msg || `Could not ${action} the payment.` });
      return;
    }
    setMessage(
      action === 'cancel'
        ? { tone: 'success', text: 'Payment cancelled — the invoice stays open.' }
        : { tone: 'info', text: `Status refreshed: ${intentMeta(result.data.state).label.toLowerCase()} (${result.data.Status}).` }
    );
    await load();
    onChanged?.();
  };

  const active = intents.find((intent) => intent.active);
  const rows = intents.map((intent) => [
    String(intent.CreatedAt).substring(0, 10),
    <Text as="span" fontWeight="semibold">
      {formatToCurrency(intent.Amount)}
    </Text>,
    <Text as="span" variant="bodySm">
      {intent.accountDescription || formatIban(intent.accountReference)}
    </Text>,
    <Text as="span" variant="bodySm">
      {formatIban(intent.CreditorIBAN)}
    </Text>,
    <Text as="span" tone="subdued" variant="bodySm">
      {intent.remittanceDisplay || intent.RemittanceInformation || ''}
    </Text>,
    <PaymentIntentBadge intent={intent} />,
    intent.createdBy || '',
    <ButtonGroup>
      {intent.state === 'awaiting_signature' ? (
        <Button size="slim" onClick={() => window.open(signingTarget(intent).url, '_blank', 'noopener')}>
          {signingTarget(intent).label}
        </Button>
      ) : null}
      {intent.active ? (
        <Button size="slim" loading={busyId === intent.PontoPaymentID} onClick={() => act(intent, 'refresh')}>
          Refresh
        </Button>
      ) : null}
      {intent.state === 'awaiting_signature' ? (
        <Button size="slim" tone="critical" loading={busyId === intent.PontoPaymentID} onClick={() => act(intent, 'cancel')}>
          Cancel
        </Button>
      ) : null}
    </ButtonGroup>
  ]);

  return (
    <Card padding="0">
      <Box padding="300" paddingBlockEnd="0">
        <InlineStack align="space-between" blockAlign="center">
          <CardHeading title="Bank payments (Ponto)" />
          <Link removeUnderline url="/banking">
            Open Banking
          </Link>
        </InlineStack>
        {active ? (
          <Box paddingBlockEnd="300">
            <Banner tone={active.state === 'awaiting_signature' ? 'warning' : 'info'}>
              {intentMeta(active.state).hint}
              {active.RequestedExecutionDate ? ` Requested execution: ${String(active.RequestedExecutionDate).substring(0, 10)}.` : ''}
            </Banner>
          </Box>
        ) : null}
        {message ? (
          <Box paddingBlockEnd="300">
            <Text as="p" tone={message.tone === 'critical' ? 'critical' : 'subdued'} variant="bodySm">
              {message.text}
            </Text>
          </Box>
        ) : null}
      </Box>
      <DataTable
        columnContentTypes={['text', 'numeric', 'text', 'text', 'text', 'text', 'text', 'text']}
        headings={['Sent', 'Amount', 'From account', 'To IBAN', 'Message', 'Status', 'By', '']}
        rows={rows}
        increasedTableDensity
        verticalAlign="middle"
      />
    </Card>
  );
}
